'use client';

import React, { useEffect } from 'react';
import { Search } from 'lucide-react';

/**
 * The top bar's "Jump to…" affordance and THE ⌘K binding.
 *
 * `onOpen` is a prop rather than a reach into `@/hooks/useCommandPalette`
 * (a console-only zustand store) so the staff app can mount the same control
 * over its own palette — previously it had no jump-to at all, which on a tool
 * whose whole job is finding one workspace out of hundreds was the single
 * biggest thing missing from it.
 */
export const SearchTrigger: React.FC<{ onOpen: () => void; label?: string }> = ({
  onOpen,
  label = 'Jump to...',
}) => {

  // Global Cmd+K / Ctrl+K handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpen();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onOpen]);

  return (
    <button
      onClick={onOpen}
      className="focus-ring hidden lg:flex items-center gap-2 h-8 px-3 rounded-md bg-input border border-border/40 text-ink-supporting text-sm hover:bg-surface-hover hover:border-border transition-colors min-w-[160px]"
      aria-label="Jump to a page (Cmd+K)"
    >
      <Search className="h-3.5 w-3.5 flex-shrink-0" />
      <span className="flex-1 text-left">{label}</span>
      <span className="text-xs text-ink-supporting">&#8984;K</span>
    </button>
  );
};